import React, { useEffect, useMemo, useRef, useState } from "react";
// REDUX
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import usePostCommand from "../../../customHooks/POST_requests/usePostCommand";
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import ActionCommandButton from "../Actions/components/ActionCommandButton";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionCommandOption } from "../../../interfaces/Bank.interface";
// CONSTANTS
import {
  resolveCommandEnum,
  resolveValidStateEnums,
  assertBoundCommand,
  UNBOUND_COMMAND_REASON,
  dispatchFloor,
  floorSelectionMissing,
} from "./actionButtonUtils";
import useAwaitingTargetReason from "./useAwaitingTargetReason";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { UuidT } from "../../../flatbuffers/generated/front-end/uuid";
// VERIFICATION
import useActionOutcome, { ActionVerification } from "./useActionOutcome";

/**
 * One button holding both states of a SINGLE bound command.
 *
 * `UiCmd` already carries an `active` flag, so energising and de-energising a
 * command never required two of them. Every other button type binds a second
 * command — a paired control, or the one sent on release or when a timer
 * elapses — and a row that has no meaningful opposite had nowhere to go but
 * TOGGLE, which then rendered a second button with nothing bound to it.
 *
 * The active state is deliberately LOCAL and optimistic. The controller does
 * not echo a command back as state, so the only alternative would be to leave
 * the button stateless and make the operator guess which press does what. If a
 * command is refused the next press simply sends the opposite, which is the
 * same recovery an operator has with the two-button types.
 */
export default function ActionButtonSingle({
  validStates,
  label,
  messageId,
  command = null,
  colorAction,
  carService,
  targetFloor = undefined,
  registerSend = undefined,
  expectedResponse = null,
  expectedResponseTimeout = null,
  commandMessageId = null,
}: {
  validStates: Array<string | UuidT>;
  label: string;
  messageId: string | UuidT;
  command?: ActionCommandOption | null;
  colorAction: string;
  carService: number;
  /**
   * The floor this command dispatches to. `undefined` for a command that takes
   * no floor, `null` while a floor-taking one waits on the operator's choice.
   */
  targetFloor?: number | null;
  /**
   * Register this button's send so a floor tap can invoke it.
   *
   * The floor is passed IN to the registered function rather than read from
   * `targetFloor`, and that is the point: the parent calls it in the same tick
   * it selects the floor, before its own state has updated. Reading
   * `targetFloor` there would dispatch at the PREVIOUS floor — a command sent
   * where the operator did not ask, which is precisely what the two-step
   * interaction exists to prevent.
   *
   * Registration is idempotent, so React StrictMode's double-invoke in
   * development re-registers the same wrapper rather than sending twice.
   */
  registerSend?: (send: (floorOverride?: number) => void) => void;
} & ActionVerification) {
  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [isActive, setIsActive] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { triggerPostCommand } = usePostCommand();
  const { messages } = useGetAllMessages();
  const reportOutcome = useActionOutcome({
    expectedResponse,
    expectedResponseTimeout,
    commandMessageId,
  });

  // ************************************************ */
  // MEMOIZED VALUES ******************************** */
  // null (not 0) when unbound — Control enum 0 is CarCallLock, a real command,
  // so a 0 fallback would silently fire it.
  const messageEnum = useMemo(
    () => resolveCommandEnum(messages, messageId, command),
    [messages, messageId, command],
  );

  const validStatesNumbers = useMemo(
    () => resolveValidStateEnums(messages, validStates),
    [messages, validStates],
  );

  const blockedByService = validStatesNumbers.includes(carService);
  const awaitingFloor = floorSelectionMissing(targetFloor);
  const selectFloorReason = useAwaitingTargetReason(messageEnum);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  // A ref so the registered wrapper stays stable while always calling the
  // CURRENT handler — re-registering on every render would churn the parent's
  // ref for no benefit.
  const sendRef = useRef<(floorOverride?: number) => void>(() => {});

  useEffect(() => {
    registerSend?.((floorOverride?: number) => sendRef.current(floorOverride));
  }, [registerSend]);

  const sendCommand = async (floorOverride?: number) => {
    // A refused command is reported as Blocked by the gate that refused it, and
    // the optimistic local state must NOT flip — showing the button in a state
    // the controller was never told about is exactly the lie this replaces.
    if (!assertBoundCommand(messageEnum, label, theme)) {
      reportOutcome(label, false, messageId);
      return;
    }

    const next = !isActive;
    setIsActive(next);
    const delivered = await triggerPostCommand({
      floor: dispatchFloor(floorOverride ?? targetFloor),
      command: messageEnum,
      liftType: LiftType[LiftType.ELEVATOR] as "ELEVATOR",
      active: next,
    });
    if (!delivered) setIsActive(!next);
    reportOutcome(label, delivered, messageId);
  };

  /**
   * The button's own onClick, which deliberately takes NO arguments.
   *
   * `sendCommand` is not wired to onClick directly: React would pass the click
   * EVENT as its first argument, that event would be read as the floor
   * override, and the command would go out with a SyntheticEvent where a floor
   * index belongs. Nothing throws — the button looks like it worked.
   */
  const onClickCommand = () => {
    // Result deliberately unused: sendCommand reports its own outcome through
    // the toast, and there is no caller here to hand a rejection to.
    sendCommand().catch(() => {});
  };

  sendRef.current = sendCommand;

  // A function rather than stacked ternaries: an unbound command outranks a
  // missing target, since no floor or output number can make it sendable.
  const disabledReason = (): string | undefined => {
    if (messageEnum == null) return UNBOUND_COMMAND_REASON;
    if (awaitingFloor) return selectFloorReason;
    return undefined;
  };

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <ActionCommandButton
      label={label}
      color={colorAction}
      outlined={!isActive}
      disabled={messageEnum == null || blockedByService || awaitingFloor}
      disabledReason={disabledReason()}
      onClick={onClickCommand}
    />
  );
}
